(function () {
  const SAMPLE_PRODUCT_ID = 15504923656567;
  const SAMPLE_PROPERTY = "_xmiles_free_sample";
  const DEFAULT_THRESHOLD = 4000;
  const MESSAGE_KEY = "xmiles_sample_checkout_message";

  let isHandlingCheckout = false;
  let bypassGuard = false;

  function getRoot() {
    const shopifyRoot =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    const root = shopifyRoot || "/";
    return root.endsWith("/") ? root : `${root}/`;
  }

  function endpoint(path) {
    return `${getRoot()}${path}`;
  }

  function isSampleLine(item) {
    return Boolean(
      item &&
        (Number(item.product_id) === SAMPLE_PRODUCT_ID ||
          String(item.properties && item.properties[SAMPLE_PROPERTY]) === "true")
    );
  }

  function getSampleThreshold(sampleItems) {
    const threshold = parseInt(
      sampleItems[0] &&
        sampleItems[0].properties &&
        sampleItems[0].properties._sample_offer_threshold,
      10
    );

    return threshold || DEFAULT_THRESHOLD;
  }

  function getSampleLimit(sampleItems) {
    const limit = parseInt(
      sampleItems[0] &&
        sampleItems[0].properties &&
        sampleItems[0].properties._sample_offer_limit,
      10
    );

    return Math.min(limit || 3, 3);
  }

  function getQualifyingTotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items
      .filter((item) => {
        const type = String(item.product_type || "").toLowerCase();
        return !isSampleLine(item) && type !== "gift card" && item.handle !== "xmiles-plus";
      })
      .reduce(
        (sum, item) =>
          sum + Number(item.final_line_price || item.line_price || 0),
        0
      );
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, "\u00a3{{amount}}");
    }

    const amount = (Number(cents || 0) / 100).toFixed(2);
    return `\u00a3${amount}`;
  }

  async function fetchCart() {
    const response = await fetch(endpoint("cart.js"), {
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error("Cart could not be checked");
    }

    return response.json();
  }

  async function changeLine(item, quantity) {
    const response = await fetch(endpoint("cart/change.js"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: item.key,
        quantity,
      }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.description || body.message || "Cart update failed");
    }
  }

  async function removeSampleLines(sampleItems) {
    for (const item of sampleItems) {
      await changeLine(item, 0);
    }
  }

  async function normalizeSampleLines(sampleItems) {
    const limit = getSampleLimit(sampleItems);
    const changes = [];

    sampleItems.forEach((item, index) => {
      if (index >= limit) {
        changes.push({ item, quantity: 0 });
      } else if (Number(item.quantity) > 1) {
        changes.push({ item, quantity: 1 });
      }
    });

    for (const change of changes) {
      await changeLine(change.item, change.quantity);
    }
  }

  function isCheckoutControl(element) {
    if (!element || !element.closest) return null;

    return element.closest(
      'button[name="checkout"], input[name="checkout"], [data-atc-button-checkout], .btn-checkout, a[href*="/checkout"]'
    );
  }

  function getCheckoutTrigger(event) {
    if (bypassGuard) return null;

    if (event.type === "submit") {
      const form = event.target;
      const submitter = event.submitter || document.activeElement;
      const action = form && form.getAttribute ? form.getAttribute("action") || "" : "";

      if (!form || (!form.hasAttribute("data-cart-form") && action.indexOf("/cart") === -1)) {
        return null;
      }

      const control = isCheckoutControl(submitter);
      return control ? { control, form } : null;
    }

    const control = isCheckoutControl(event.target);
    if (!control) return null;

    return {
      control,
      form: control.form || control.closest("form"),
    };
  }

  function showMessage(message) {
    const existing = document.querySelector("[data-sample-checkout-warning]");
    const warning = existing || document.createElement("div");

    warning.className = "xmiles-sample-checkout-warning";
    warning.setAttribute("data-sample-checkout-warning", "");
    warning.textContent = message;

    if (existing) return;

    const drawerActions = document.querySelector("[data-cart-drawer] .cart-actions");
    if (drawerActions) {
      drawerActions.prepend(warning);
      return;
    }

    const cartFlow = document.querySelector(".cart-flow");
    if (cartFlow && cartFlow.parentNode) {
      cartFlow.parentNode.insertBefore(warning, cartFlow);
      return;
    }

    document.body.prepend(warning);
  }

  function persistMessage(message) {
    try {
      window.sessionStorage.setItem(MESSAGE_KEY, message);
    } catch (error) {
      console.warn("Sample checkout message could not be stored", error);
    }
  }

  function showPersistedMessage() {
    try {
      const message = window.sessionStorage.getItem(MESSAGE_KEY);
      if (!message) return;

      window.sessionStorage.removeItem(MESSAGE_KEY);
      showMessage(message);
    } catch (error) {
      console.warn("Sample checkout message could not be read", error);
    }
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  function continueToCheckout(trigger) {
    bypassGuard = true;

    if (trigger.form) {
      if (!trigger.form.querySelector('input[type="hidden"][name="checkout"]')) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "checkout";
        input.value = "Checkout";
        trigger.form.appendChild(input);
      }

      HTMLFormElement.prototype.submit.call(trigger.form);
      return;
    }

    const href =
      trigger.control && trigger.control.getAttribute
        ? trigger.control.getAttribute("href")
        : "";

    window.location.href = href || endpoint("checkout");
  }

  async function validateCheckout() {
    const cart = await fetchCart();
    const sampleItems = (cart.items || []).filter(isSampleLine);

    if (!sampleItems.length) return true;

    const threshold = getSampleThreshold(sampleItems);
    if (getQualifyingTotal(cart) >= threshold) {
      await normalizeSampleLines(sampleItems);
      return true;
    }

    await removeSampleLines(sampleItems);

    const message = `Free samples are only available on orders over ${formatMoney(
      threshold
    )}, excluding sample products. We have removed them from your cart.`;

    persistMessage(message);
    showMessage(message);
    document.dispatchEvent(new CustomEvent("cart:modified"));
    document.dispatchEvent(new CustomEvent("cart:drawer:open"));

    if (isCartPage()) {
      window.location.reload();
    }

    return false;
  }

  async function handleCheckout(event) {
    const trigger = getCheckoutTrigger(event);
    if (!trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (isHandlingCheckout) return;

    isHandlingCheckout = true;

    try {
      const canCheckout = await validateCheckout();
      if (canCheckout) {
        continueToCheckout(trigger);
      }
    } catch (error) {
      console.error("Sample checkout validation failed:", error);
      showMessage("We could not check your free samples. Please try checkout again.");
    } finally {
      isHandlingCheckout = false;
    }
  }

  document.addEventListener("click", handleCheckout, true);
  document.addEventListener("submit", handleCheckout, true);
  document.addEventListener("DOMContentLoaded", showPersistedMessage);
})();
